import MuiTextField, { TextFieldProps as MuiTextFieldProps } from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { red } from "designSystem/styles/color";
import React, { FocusEvent, forwardRef, FunctionComponent, KeyboardEvent, useRef } from "react";
import { useLocale } from "designSystem/locale";
import { textFieldInnerOutlineStyle, textfieldStyle } from "designSystem/styles/style-functions";
import { TEXT_FIELD_ERROR_TOP_POSITION } from "designSystem/theme";
import clsx from "clsx";

const PREFIX = "TextField";

const classes = {
	borderless: `${PREFIX}borderless`,
	input: `${PREFIX}input`,
	error: `${PREFIX}error`,
	innerBorderOutline: `${PREFIX}innerBorderOutline`,
};

const StyledMuiTextField = styled(MuiTextField)(({
	theme,
}) => ({
	[`& .${classes.borderless}`]: {
		border: "none",
		paddingLeft: 0,
		paddingRight: 0,
	},

	[`& .${classes.input}`]: textfieldStyle(theme),

	[`& .${classes.error}`]: {
		borderColor: `${red[600]} !important`,
	},

	[`& .${classes.innerBorderOutline}`]: textFieldInnerOutlineStyle(theme),
}));

export interface TextFieldProps extends Omit<MuiTextFieldProps, "variant"> {
	/** indicate if component should have no border */
	borderless?: boolean;

	/** data-csi-automation label for input */
	dataCsiAutomation?: string;

	/** localization resource to use in helper text */
	helperTextRes?: string;

	/** parameters to use for helper text resource */
	helperTextResParams?: Array<number | string>;

	/** event handler for down arrow key */
	onDown?: (event: KeyboardEvent<HTMLInputElement>) => void;

	/** event handler for enter key */
	onEnter?: (event: KeyboardEvent<HTMLInputElement>) => void;

	/** event handler for escape key */
	onEscape?: (event: KeyboardEvent<HTMLInputElement>) => void;

	/** event handler for tab key */
	onTab?: (event: KeyboardEvent<HTMLInputElement>) => void;

	/** event handler for up arrow key */
	onUp?: (event: KeyboardEvent<HTMLInputElement>) => void;

	/** localization resource to use for placeholder */
	placeholderRes?: string;

	/** parameters to use for placeholder resource */
	placeholderResParams?: Array<number | string>;

	/** localization resource to use to lookup text; can be simple resource or type resource (i.e. "User.Name") */
	res?: string;

	/** parameters to pass if looking up resource */
	resParams?: Array<number | string>;

	/** indicate if text in field should be selected when focused */
	selectOnFocus?: boolean;
}

const TextField: FunctionComponent<TextFieldProps> = forwardRef((props, ref) => {
	const loc = useLocale();
	const firstFocus = useRef(true);
	const {
		borderless,
		dataCsiAutomation,
		error,
		helperText,
		helperTextRes,
		helperTextResParams,
		label,
		onBlur,
		onDown,
		onEnter,
		onEscape,
		onFocus,
		onTab,
		onUp,
		placeholder,
		placeholderRes,
		placeholderResParams,
		res,
		resParams,
		selectOnFocus,
		inputProps,
		InputProps,
		...otherProps
	} = props;

	function _onBlur(event: FocusEvent<HTMLInputElement>): void {
		// reset first focus boolean and call original onBlur, if any
		firstFocus.current = true;
		onBlur?.(event);
	};

	function _onFocus(event: FocusEvent<HTMLInputElement>): void {
		// select value in input on focus and call original onFocus, if any
		if (selectOnFocus && firstFocus.current) {
			const { currentTarget } = event;

			// delay selection as the input value appears to be empty initially;
			// may need to find another way to do this???
			setTimeout(() => {
				currentTarget.select();
			}, 1);

			// only want to selectOnFocus the first time clicked until it is blurred
			firstFocus.current = false;
		}

		onFocus?.(event);
	};

	function _onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
		if (onTab && event.key==="Tab") {
			event.preventDefault();
		}
	};

	function _onKeyUp(event: KeyboardEvent<HTMLInputElement>): void {
		switch (event.key) {
			case "Enter":
				onEnter?.(event);
				break;
			case "Escape":
				onEscape?.(event);
				break;
			case "Tab":
				onTab?.(event);
				break;
			case "ArrowDown":
				onDown?.(event);
				break;
			case "ArrowUp":
				onUp?.(event);
				break;
		}
	};

	// MUI doesn't expect a notchedOutline class on the input props, but the legacy props do work, so cast to any
	const notchedOutlineClass = (InputProps as any)?.classes?.notchedOutline;
	const propLabel = label || (res && loc.resource(res, resParams));

	return (
		<StyledMuiTextField
			data-testid="TextField"
			{...otherProps}
			FormHelperTextProps={{
				...otherProps.FormHelperTextProps,
				sx: {
					top: !propLabel ? TEXT_FIELD_ERROR_TOP_POSITION : undefined,
					zIndex: 1,
					...otherProps.FormHelperTextProps?.sx,
				},

			}}
			InputProps={{
				...InputProps,
				classes: {
					...(InputProps?.classes || {}),
					notchedOutline: clsx(classes.innerBorderOutline, notchedOutlineClass),
					root: clsx(classes.input, borderless && classes.borderless, error && classes.error, InputProps?.classes?.root),
				},
			}}
			error={error}
			helperText={helperText || (helperTextRes && loc.resource(helperTextRes, helperTextResParams))}
			inputProps={{
				...inputProps,
				"data-csi-automation": dataCsiAutomation,
			}}
			label={propLabel}
			onBlur={_onBlur}
			onFocus={_onFocus}
			onKeyDown={_onKeyDown}
			onKeyUp={_onKeyUp}
			placeholder={placeholder || (placeholderRes && loc.resource(placeholderRes, placeholderResParams))}
			ref={ref}
			variant="outlined"
		/>
	);
});

export default TextField;